import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { Bildungsangebot } from '../model';
import { BildungsangebotPage } from '../model/bildungsangebot-page';
import { CommonStateService } from './state/common-state.service';
import { UrlService } from './state/url.service';

/**
 * Bündelt alle Parameter reaktiv und ruft die zugehörigen Seiten vom Backend ab und stellt
 * diese als Observable bereit.
 */
@Injectable({
  providedIn: 'root'
})
export class AccumulatedBildungsangeboteService {
  private readonly urlService = inject(UrlService);
  private readonly commonStateService = inject(CommonStateService);

  private readonly maxPages = 10;

  private readonly _bildungsAngebotPages$ = new BehaviorSubject<BildungsangebotPage[]>([]);
  public bildungsAngebotPages$ = this._bildungsAngebotPages$.asObservable();

  constructor() {
    this.commonStateService.selectedAngebotId$.subscribe(this.setCurrentPageByAngebot);
  }

  public addAngebotePage = (newPage: BildungsangebotPage) => {
    // Es werden hier Pages Konkateniert (Seite 2 wird aufgerufen und mit Seite 0 und 1 verknüpft)
    let bildungsAngebotPages = this._bildungsAngebotPages$.getValue();

    if (!newPage._embedded || newPage._embedded?.bildungsangebotDTOList?.length === 0) {
      // If new page has no angebote
      bildungsAngebotPages = [];
    } else if (
      newPage.page.number === 0 &&
      bildungsAngebotPages.find((page) => page.page.number === 0)
    ) {
      // if page already included and newpage is 0 => reset all pages
      bildungsAngebotPages = [newPage];
    } else if (
      bildungsAngebotPages.findIndex((page) => page.page.number === newPage.page.number) >= 0
    ) {
      // Replace page
      const oldItemIndex = bildungsAngebotPages.findIndex(
        (page) => page.page.number === newPage.page.number
      );
      bildungsAngebotPages[oldItemIndex] = newPage;
      bildungsAngebotPages = [...bildungsAngebotPages];
    } else if (
      newPage.page.number === 0 &&
      bildungsAngebotPages.length > 0 &&
      newPage.page.totalElements != bildungsAngebotPages[0].page.totalElements
    ) {
      // Die neue Seite die rein kommt ist eine Seite 0 (Neue Treffer/Suche/Filter)
      // Gibt es weitere Seiten die schon geladen wurden? Wenn Ja, schau ob es die gleiche Anzahl an Treffern hat wie die Neue Seite.
      // Wenn das der FAll ist, dann wurde zb. auf eine "vorherige Seite" navigiert
      // Ist das nicht der Fall - Treffermengen sind unterschiedlich, dann ist das eine Suche/Filter
      // und wir starten von der neuen Seite 0.
      bildungsAngebotPages = [newPage];
    } else {
      // Add new Page
      bildungsAngebotPages = [...bildungsAngebotPages, newPage];
    }

    // Sort by page number
    bildungsAngebotPages.sort((a, b) => a.page.number - b.page.number);

    // remove page if more than maxPages
    if (bildungsAngebotPages.length > this.maxPages) {
      const highestPage = bildungsAngebotPages.reduce((maxObject, currentObject) => {
        return currentObject.page.number > maxObject.page.number ? currentObject : maxObject;
      }, bildungsAngebotPages[0]);
      if (highestPage.page.number === newPage.page.number) {
        bildungsAngebotPages.shift();
      } else {
        bildungsAngebotPages.pop();
      }
    }

    this._bildungsAngebotPages$.next([...bildungsAngebotPages]);
  };

  public bildungsangebote$ = this._bildungsAngebotPages$.pipe(
    // Sort by page number
    map((pages) => {
      pages.sort((a, b) => a.page.number - b.page.number);
      return pages;
    }),
    // Flatten Page items
    map((pages) => {
      const items = pages.map((page) => page._embedded?.bildungsangebotDTOList);
      return items.flat();
    })
  );

  // Searches all loaded angebot pages for the one containing the current angebot.
  // Then sets the "seite" url queryparameter to the containing page.
  // This is needed to load the correct page after reloading the application
  private readonly setCurrentPageByAngebot = async (angebotId: number | null) => {
    if (angebotId) {
      const seite = await firstValueFrom(
        this._bildungsAngebotPages$.pipe(
          map((pages) => {
            const angebotPage = pages.find((page) =>
              page._embedded.bildungsangebotDTOList.find((angebot) => angebot.id === angebotId)
            );
            return angebotPage?.page.number;
          })
        )
      );

      if (seite != undefined) {
        this.urlService.updateUrlQueryParams({
          [UrlService.AKTUELLE_SEITE]: seite !== 0 ? seite : null
        });
      }
    }
  };

  public selectedAngebot$: Observable<Bildungsangebot> = combineLatest([
    this.commonStateService.selectedAngebotId$,
    this.bildungsangebote$
  ]).pipe(
    map(([selectedAngebotId, bildungsangebote]) => {
      const angebot = bildungsangebote?.find(
        (bildungsangebot) => bildungsangebot.id === selectedAngebotId
      );
      return angebot!;
    })
  );
}
